import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { Send, ArrowLeftRight, StickyNote, XCircle, AlertTriangle, Bot, User, Headphones } from 'lucide-react'
import { cn } from '@/utils/cn'
import { format } from 'date-fns'
import { auth } from '@/firebase'
import { API_BASE_URL } from '@/config'
import type { AgentSession, AgentMessage } from '@/hooks/useAgentWebSocket'
import { useTranslation } from '@/i18n'
import MarkdownMessage from '@/components/MarkdownMessage'

interface ChatPanelProps {
  session: AgentSession | null
  messages: AgentMessage[]
  onSendMessage: (sessionId: string, content: string) => void
  onTyping: (sessionId: string) => void
  isTyping: boolean
}

export default function ChatPanel({ session, messages, onSendMessage, onTyping, isTyping }: ChatPanelProps) {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  useEffect(() => {
    setInput('')
    textareaRef.current?.focus()
  }, [session?.id])

  function handleSend() {
    if (!session || !input.trim()) return
    onSendMessage(session.id, input.trim())
    setInput('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleInput(value: string) {
    setInput(value)
    if (session) onTyping(session.id)
  }

  async function handleAction(action: 'back-to-ai' | 'close') {
    if (!session) return
    const token = await auth.currentUser?.getIdToken()
    const apiBase = API_BASE_URL
    const endpoint = action === 'back-to-ai' ? 'back-to-ai' : 'close'
    try {
      await fetch(`${apiBase}/agents/sessions/${session.id}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      // Error will be handled via WS session_update
    }
  }

  async function handleAddNote() {
    if (!session) return
    const note = window.prompt(t('agent.addNotePrompt'), session.note || '')
    if (note === null) return
    const token = await auth.currentUser?.getIdToken()
    const apiBase = API_BASE_URL
    try {
      await fetch(`${apiBase}/agents/sessions/${session.id}/note`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note }),
      })
    } catch {
      // Ignore
    }
  }

  if (!session) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-400">
          <Headphones className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{t('agent.selectSession')}</p>
          <p className="text-xs mt-1">{t('agent.selectSessionHint')}</p>
        </div>
      </div>
    )
  }

  const renderMessages = () => {
    const elements: React.ReactNode[] = []
    let lastMode: string | null = null

    for (const msg of messages) {
      if (msg.role === 'agent' && lastMode !== 'agent') {
        elements.push(
          <div key={`divider-${msg.id}`} className="flex items-center gap-2 py-2">
            <div className="flex-1 h-px bg-green-200" />
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">{t('agent.humanAgentConnected')}</span>
            <div className="flex-1 h-px bg-green-200" />
          </div>
        )
      }

      if (msg.role === 'system') {
        elements.push(
          <div key={msg.id} className="flex items-center gap-2 py-2">
            <div className="flex-1 h-px bg-orange-200" />
            <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {msg.content}
            </span>
            <div className="flex-1 h-px bg-orange-200" />
          </div>
        )
        lastMode = 'system'
        continue
      }

      const isUser = msg.role === 'user'
      const isAI = msg.role === 'assistant'
      const isAgent = msg.role === 'agent'

      elements.push(
        <div key={msg.id} className={cn('flex gap-2.5', isUser ? 'justify-end' : 'justify-start')}>
          {!isUser && (
            <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0', isAI ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-600')}>
              {isAI ? <Bot className="h-4 w-4" /> : <Headphones className="h-3.5 w-3.5" />}
            </div>
          )}
          <div className={cn('max-w-[65%]')}>
            {isAgent && msg.agentName && (
              <span className="text-[10px] text-green-600 mb-0.5 block">{msg.agentName}</span>
            )}
            <div className={cn('rounded-lg px-3 py-2 text-sm', isUser && 'bg-indigo-600 text-white', isAI && 'bg-white border border-gray-200 text-gray-800', isAgent && 'bg-green-50 border border-green-200 text-gray-800')}>
              <MarkdownMessage content={msg.content} />
            </div>
            <span className="text-[10px] text-gray-400 mt-0.5 block">
              {(() => { try { return format(new Date(msg.timestamp), 'HH:mm') } catch { return '' } })()}
            </span>
          </div>
          {isUser && (
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-600 flex-shrink-0">
              <User className="h-3.5 w-3.5" />
            </div>
          )}
        </div>
      )

      lastMode = msg.role
    }

    return elements
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="px-4 py-2.5 border-b border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900">
              {session.projectName || t('agent.session')} · {t('agent.userChat')}
            </h3>
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', session.mode === 'human' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>
              {session.mode === 'human' ? t('agent.humanMode') : t('agent.queuedMode')}
            </span>
          </div>
          {session.escalationReason && (
            <p className="text-xs text-orange-600 mt-0.5 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {t('agent.escalationReason')}：{session.escalationReason}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => handleAction('back-to-ai')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
            <ArrowLeftRight className="h-3.5 w-3.5" />
            {t('agent.backToAI')}
          </button>
          <button onClick={handleAddNote} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
            <StickyNote className="h-3.5 w-3.5" />
            {t('agent.addNote')}
          </button>
          <button onClick={() => handleAction('close')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
            <XCircle className="h-3.5 w-3.5" />
            {t('agent.closeSession')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 text-xs py-8">{t('agent.noMessages')}</div>
        )}
        {renderMessages()}
        {isTyping && (
          <div className="flex items-center gap-2 text-gray-400">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-3.5 w-3.5 text-indigo-500" />
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-200 bg-white p-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => handleInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('agent.inputPlaceholder')}
            rows={1}
            className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent max-h-32"
            onInput={e => {
              const el = e.currentTarget
              el.style.height = 'auto'
              el.style.height = Math.min(el.scrollHeight, 128) + 'px'
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className={cn('p-2 rounded-lg transition-colors flex-shrink-0', input.trim() ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
